import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Autocomplete,
	Button,
	Card,
	CardContent,
	Grid,
	Typography,
	Box,
	Stack
} from "@mui/material";
import TextField from "@mui/material/TextField";
import floatParser from "../../../helpers/floatParser";
import {ICariKayit, IKalem, IKisi} from "../../../constants/types/logbook";
import IconButton from "@mui/material/IconButton";
import {Delete, Undo, AccountBalance, AddCircleOutline, ExpandMore} from "@mui/icons-material";

interface CariCardProps {
	guncelle: any;
	silinmis: boolean;
	guncellenmis: boolean;
	ekle: boolean;
	cariler: IKalem<ICariKayit>;
	cariIndex: number;
	setCariler: any;
	calisanlar: IKisi[];
}

function CariCard({
	                  guncelle,
	                  silinmis,
	                  guncellenmis,
	                  ekle,
	                  cariler,
	                  cariIndex,
	                  setCariler,
	                  calisanlar,
                  }: CariCardProps) {
	const cari = ekle ? cariler.eklenenListe[cariIndex] : cariler.data[cariIndex];
	return (
		<Grid item xs={12} key={"cari-" + cariIndex}>
			<Card
				elevation={0}
				sx={{
					border: '1px solid',
					borderColor: ekle ? 'success.main' : guncellenmis ? 'info.light' : silinmis ? 'error.light' : 'rgba(0,0,0,0.08)',
					backgroundColor: ekle ? '#f1fff1' : guncellenmis ? 'info.light' : silinmis ? '#fff5f5' : '#fff',
					borderStyle: ekle ? 'dashed' : 'solid',
					borderRadius: '12px',
					transition: 'all 0.2s'
				}}
			>
				<CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
					<Grid container spacing={1} alignItems="center">
						<Grid item xs={12} sm={3}>
							<Autocomplete
								size={"small"}
								disablePortal
								disabled={silinmis || (!!cari.kisi && !calisanlar.find(q => q.id === cari.kisi)?.aktif)}
								options={["Yeni", "Ödeme"]}
								disableClearable
								renderInput={(params) => <TextField {...params} label="Kayıt Tipi"/>}
								value={cari.hareket_tip}
								onChange={(_, value) => {
									const guncel = {...cari, hareket_tip: value};
									guncelle(guncel);
								}}
							/>
						</Grid>
						<Grid item xs={12} sm={4}>
							<Autocomplete
								size={"small"}
								disablePortal
								disabled={silinmis || (!!cari.kisi && !calisanlar.find(q => q.id === cari.kisi)?.aktif)}
								options={calisanlar.filter(x => x.aktif).map(q => q.id)}
								getOptionLabel={option => calisanlar.find(q => q.id === option)?.rumuz ?? ""}
								disableClearable
								renderInput={(params) => <TextField {...params} label="Hesap Sahibi"/>}
								value={cari.kisi}
								onChange={(_, value) => {
									const guncel = {...cari, kisi: value};
									guncelle(guncel);
								}}
							/>
						</Grid>
						<Grid item xs={12} sm={3}>
							<TextField
								size={"small"}
								fullWidth
								disabled={silinmis || (!!cari.kisi && !calisanlar.find(q => q.id === cari.kisi)?.aktif)}
								type={"number"}
								label={"Tutar"}
								InputProps={{ endAdornment: <Typography variant="caption" sx={{ ml: 1, fontWeight: 700 }}>₺</Typography> }}
								value={"" + cari.miktar}
								onChange={(event) => {
									const guncel = {...cari, miktar: floatParser(event.target.value)};
									guncelle(guncel);
								}}
							/>
						</Grid>
						<Grid item xs={12} sm={2}>
							<Stack direction="row" spacing={1} justifyContent={{ xs: 'center', sm: 'flex-end' }}>
								<IconButton
									size="small"
									disabled={(!ekle && silinmis) || (!!cari.kisi && !calisanlar.find(q => q.id === cari.kisi)?.aktif)}
									onClick={() => {
										if (ekle) {
											setCariler({
												...cariler,
												eklenenListe: cariler.eklenenListe.filter((_, index) => index !== cariIndex),
											})
										} else {
											setCariler({
												...cariler,
												silinecekIdListe: [...cariler.silinecekIdListe, cari.id ?? 0],
											})
										}
									}}
									color={"error"}
									sx={{ bgcolor: 'error.light', '&:hover': { bgcolor: '#ffcdd2' } }}
								>
									<Delete fontSize="small" />
								</IconButton>
								{!ekle && (
									<IconButton
										size="small"
										disabled={!guncellenmis && !silinmis}
										onClick={() => {
											if (guncellenmis) {
												setCariler({
													...cariler,
													guncellenecekIdListe: cariler.guncellenecekIdListe.filter(id => id !== cari.id),
												})
											} else {
												setCariler({
													...cariler,
													silinecekIdListe: cariler.silinecekIdListe.filter(id => id !== cari.id),
												})
											}
										}}
										color={"info"}
										sx={{ bgcolor: 'info.light', '&:hover': { bgcolor: '#b3e5fc' } }}
									>
										<Undo fontSize="small" />
									</IconButton>
								)}
							</Stack>
						</Grid>
					</Grid>
				</CardContent>
			</Card>
		</Grid>
	);
}

const Cariler = (
	{
		cariler,
		setCariler,
		calisanlar,
	}: {
		cariler: IKalem<ICariKayit>,
		setCariler: any,
		calisanlar: IKisi[],
	}) => {
	return (
		<Accordion
			elevation={0}
			sx={{
				backgroundColor: 'background.paper',
				borderRadius: '16px !important',
				border: '1px solid rgba(0,0,0,0.05)',
				mb: 1,
				overflow: 'hidden',
				'&:before': { display: 'none' }
			}}
		>
			<AccordionSummary expandIcon={<ExpandMore />}>
				<Stack direction="row" spacing={1.5} alignItems="center">
					<AccountBalance color="primary" />
					<Typography variant="h6" fontWeight={800} sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
						Cari Hesaplar
					</Typography>
					<Typography variant="caption" sx={{ bgcolor: 'primary.main', color: 'white', px: 1, py: 0.2, borderRadius: 1, fontWeight: 700 }}>
						{cariler.data.length + cariler.eklenenListe.length} Kayıt
					</Typography>
				</Stack>
			</AccordionSummary>
			<AccordionDetails sx={{ bgcolor: '#fcfcfc', p: { xs: 1, md: 1.5 } }}>
				<Grid container spacing={1}>
				{
					cariler.data.map((cari, cariIndex) => {
							const guncellenmis = cariler.guncellenecekIdListe.includes(cari.id ?? -1);
							const silinmis = cariler.silinecekIdListe.includes(cari.id ?? -1);
							if (guncellenmis) cari = cariler.guncellenecekListe[cari.id ?? 0];
							const guncelle = (guncel: any) => {
								setCariler({
									...cariler,
									guncellenecekIdListe: [...(guncellenmis ? cariler.guncellenecekIdListe : [...cariler.guncellenecekIdListe, cari.id ?? 0])],
									guncellenecekListe: {...cariler.guncellenecekListe, [cari.id ?? 0]: guncel}
								})
							}
							return (
								<CariCard
									key={"cari-" + cariIndex}
									silinmis={silinmis}
									guncellenmis={guncellenmis}
									guncelle={guncelle}
									ekle={false}
									cariler={cariler}
									cariIndex={cariIndex}
									setCariler={setCariler}
									calisanlar={calisanlar}
								/>
							)
						}
					)
				}
				{
					cariler.eklenenListe.map((cari, cariIndex) => {
							const guncelle = (guncel: any) => {
								setCariler({
									...cariler,
									eklenenListe: cariler.eklenenListe.map((c, index) => {
										if (index === cariIndex) return guncel;
										return c;
									})
								})
							}
							return (
								<CariCard
									key={"cari-ekle-" + cariIndex}
									silinmis={false}
									guncellenmis={false}
									guncelle={guncelle}
									ekle={true}
									cariler={cariler}
									cariIndex={cariIndex}
									setCariler={setCariler}
									calisanlar={calisanlar}
								/>
							)
						}
					)
				}
				<Grid item xs={12} sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
					<Button
						variant="outlined"
						startIcon={<AddCircleOutline />}
						onClick={() => {
							setCariler({
								...cariler,
								eklenenListe: [
									...cariler.eklenenListe,
									{
										hareket_tip: "Yeni",
										miktar: 0,
										kisi: 0,
									}]
							})
						}}
						sx={{ 
							borderRadius: '10px', 
							textTransform: 'none', 
							fontWeight: 700,
							px: 4,
							borderWidth: '2px',
							'&:hover': { borderWidth: '2px' }
						}}
					>
						Yeni Kayıt Ekle
					</Button>
				</Grid>
			</Grid>
		</AccordionDetails>
	</Accordion>
	);
}

export default Cariler;